// Share Trade-in — TanStack Query mutation hook for POST
// /api/v1/vault/trade-in.
//
// Body: { share_ids: string[] }. On success the server has burned the
// selected shares + minted a random sealed dossier; the caller
// invalidates ["vault-items"] so the grid refetches (burned shares gone,
// new sealed dossier present).
//
// Branched error envelope per `code`:
//   - TRADE_IN_INSUFFICIENT_POINTS (422) — selection < 10 points.
//   - TRADE_IN_SHARE_NOT_ELIGIBLE (409) — one share not owned / already
//     retired / on an active listing (see `reason`).
//   - TRADE_IN_NO_LIVE_DROPS (409) — nothing to mint right now.
//   - UNAUTHORIZED (401) — stale session.
//
// No onSuccess invalidation in the hook itself — the consumer owns it
// (mirrors useCancelShareListing).

import { useMutation, type UseMutationResult } from "@tanstack/react-query";

import { attachRequestIdToScope } from "../../lib/api-client";
import type { TradeInRequest, TradeInResponseData } from "./vaultTypes";

export interface TradeInError {
  code: string;
  message: string;
  status: number;
  reason?: string;
}

async function postTradeIn(
  variables: TradeInRequest,
): Promise<TradeInResponseData> {
  const response = await fetch("/api/v1/vault/trade-in", {
    method: "POST",
    credentials: "include",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(variables),
  });
  attachRequestIdToScope(response);
  if (!response.ok) {
    let detail: TradeInError = {
      code: "TRADE_IN_FAILED",
      message: `Trade-in failed (HTTP ${response.status})`,
      status: response.status,
    };
    try {
      const raw = await response.json();
      if (raw?.error) {
        detail = {
          code: raw.error.code,
          message: raw.error.message,
          status: response.status,
          reason: raw.error.reason,
        };
      }
    } catch {
      // Body not JSON — keep default.
    }
    throw detail;
  }
  const body = (await response.json()) as { data: TradeInResponseData };
  return body.data;
}

export function useTradeIn(): UseMutationResult<
  TradeInResponseData,
  TradeInError,
  TradeInRequest
> {
  return useMutation<TradeInResponseData, TradeInError, TradeInRequest>({
    mutationFn: postTradeIn,
    // NO onSuccess — caller invalidates ["vault-items"].
  });
}
